import React from "react";
import { GoogleBusinessLogo } from "../../../client/dashboard/platformCatalog";
import { useTranslation } from "../../../i18n";

const MAX_VISIBLE = 3;

function shortAuthor(name, anonymousLabel) {
  if (!name || name === "—" || name === anonymousLabel) return anonymousLabel;
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1].charAt(0)}...`;
  }
  return name.length > 8 ? `${name.slice(0, 8)}...` : name;
}

function shortSnippet(text) {
  const snippet = (text || "").trim();
  if (!snippet) return "";
  const cut = snippet.length > 18 ? `${snippet.slice(0, 18)}...` : snippet;
  return `"${cut}"`;
}

/**
 * Compact inline preview of reviews (for table rows / collapsed case headers).
 */
const DeletionCaseReviewPreviewStrip = ({
  items = [],
  onViewAll,
  className = "",
  variant = "default",
}) => {
  const { t } = useTranslation();
  const anonymousLabel = t("deletionCaseUi.anonymous");

  if (!items.length) {
    return (
      <span
        className={[
          variant === "enterprise" ? "text-label-sm text-on-surface-variant" : "text-[12px] text-[#94a3b8]",
          className,
        ].join(" ")}
      >
        {t("deletionCaseUi.noReviews")}
      </span>
    );
  }

  const visible = items.slice(0, MAX_VISIBLE);
  const rest = items.length - visible.length;

  if (variant === "enterprise") {
    return (
      <div
        className={`flex flex-wrap gap-2 ${className}`}
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        {visible.map((review) => {
          const snippet = shortSnippet(review.text);
          return (
            <div
              key={review.id ?? review.reviewId}
              title={`${review.author} · ${review.rating}★${review.text ? ` — ${review.text}` : ""}`}
              className="flex cursor-default items-center gap-2 rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 py-2 transition-colors hover:border-secondary"
            >
              <GoogleBusinessLogo className="flex h-5 w-5 items-center justify-center rounded-full shadow-sm" />
              <span className="text-xs font-semibold">{review.rating}★</span>
              <span className="max-w-[120px] truncate text-xs text-on-surface-variant">
                {shortAuthor(review.author, anonymousLabel)}
                {snippet ? ` ${snippet}` : ""}
              </span>
            </div>
          );
        })}
        {rest > 0 ? (
          <button
            type="button"
            onClick={onViewAll}
            className="flex items-center rounded-lg bg-secondary-container/30 px-3 py-2 text-xs font-bold text-on-secondary-container"
          >
            {t("deletionCaseUi.moreReviews", { count: rest })}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      {visible.map((review) => {
        const snippet = (review.text || "").trim();
        const short =
          snippet.length > 36 ? `${snippet.slice(0, 36)}…` : snippet || "—";
        return (
          <span
            key={review.id ?? review.reviewId}
            title={`${review.author} · ${review.rating}★${snippet ? ` — ${snippet}` : ""}`}
            className="inline-flex max-w-[200px] items-center gap-1.5 rounded-full border border-[#e8ecf0] bg-white px-2.5 py-1 text-[11px] text-[#475569] shadow-sm"
          >
            <span className="shrink-0 font-semibold text-amber-500">
              {review.rating}★
            </span>
            <span className="truncate font-medium text-[#0f172a]">
              {review.author}
            </span>
            <span className="hidden truncate text-[#94a3b8] sm:inline">
              {short}
            </span>
          </span>
        );
      })}
      {rest > 0 ? (
        <button
          type="button"
          onClick={onViewAll}
          className="rounded-full bg-[#e6f4f4] px-2.5 py-1 text-[11px] font-semibold text-[#006666] hover:bg-[#cce8e8]"
        >
          {t("deletionCaseUi.moreReviews", { count: rest })}
        </button>
      ) : onViewAll ? (
        <button
          type="button"
          onClick={onViewAll}
          className="text-[11px] font-semibold text-[#006666] hover:underline"
        >
          {t("deletionCaseUi.viewAll")}
        </button>
      ) : null}
    </div>
  );
};

export default DeletionCaseReviewPreviewStrip;
